import { useId } from 'react'
import { ComposedChart, Line, Area, XAxis, YAxis, ResponsiveContainer, Label, CartesianGrid } from 'recharts'
import { Title5 } from '../styles/Typography'
import { ChartContainer, ChartHeader, axisStyle, chartXAxisPadding } from './shared'
import { theme } from '../../theme/theme'
import styled from 'styled-components'

type DataPoint = {
  name: string
  nameMobile: string
  value: number
}

type Props = {
  isMobileDevice: boolean
  data: DataPoint[]
  title: string
  label?: string
  color?: string
  scale?: number[]
  withGradient?: boolean
}

export const SingleLineChart = ({
  isMobileDevice,
  data,
  title,
  label,
  color = theme.color.plum,
  scale = [0, 2, 4, 6, 8, 10],
  withGradient = false,
}: Props) => {
  const id = useId()
  const clipId = `clipGrid${id}`
  const gradientId = `singleLineGradient${id}`

  return (
    <ChartContainer>
      <ChartHeader>
        <Title5>{title}</Title5>
      </ChartHeader>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={data}
            margin={{
              right: isMobileDevice ? 5 : 30,
              left: isMobileDevice ? -15 : 0,
              top: 15,
              bottom: 5,
            }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x="0" y="0" width="100%" height="96%" /> {/* Klipp bort den översta delen */}
              </clipPath>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid horizontal vertical={false} stroke={theme.color.grey2} clipPath={`url(#${clipId})`} />
            <XAxis
              dataKey={isMobileDevice ? 'nameMobile' : 'name'}
              padding={chartXAxisPadding(isMobileDevice)}
              axisLine={false}
              tickLine={false}
              tick={axisStyle}
              interval={0}
            />
            <YAxis
              domain={[0, 10.5]}
              ticks={scale}
              axisLine={false}
              tickLine={false}
              tick={axisStyle}
              width={isMobileDevice ? 40 : undefined}
            >
              {!isMobileDevice && label && (
                <Label fontSize={theme.fontSize.xxsmall} dx={-20} value={label} angle={-90} />
              )}
            </YAxis>
            {withGradient && <Area type="monotone" dataKey="value" stroke="none" fill={`url(#${gradientId})`} />}
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={{ r: 4, fill: theme.color.white, stroke: color, strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </ChartContainer>
  )
}
const ChartWrapper = styled.div`
  .recharts-cartesian-grid-horizontal line:last-child {
    visibility: hidden;
  }
`
